import { create } from "zustand";
import { onboardingApi, type ChatMessage } from "../lib/api";
import { streamChat, type SSECallbacks } from "../lib/sse";
import { useAuthStore } from "./authStore";

interface OnboardingState {
  isRequired: boolean | null; // null = not yet checked
  isCheckingStatus: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  isComplete: boolean; // AI signaled wrap-up complete
  error: string | null;

  checkStatus: () => Promise<void>;
  startConversation: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  reset: () => void;
}

/**
 * Create SSE streaming callbacks for the onboarding chat.
 * Shared between startConversation and sendMessage to avoid duplication.
 */
function createStreamingCallbacks(
  set: (fn: (state: OnboardingState) => Partial<OnboardingState>) => void,
  streamingId: string,
  userMessageId?: string,
): SSECallbacks {
  return {
    onChunk: (text) => {
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === streamingId
            ? { ...m, content: m.content + text }
            : m
        ),
      }));
    },
    onDone: (metadata) => {
      set(() => ({
        isStreaming: false,
        isComplete: metadata?.onboardingComplete === true,
      }));
    },
    onError: (error) => {
      set((state) => ({
        messages: state.messages.filter(
          (m) =>
            m.id !== streamingId &&
            (userMessageId ? m.id !== userMessageId : true),
        ),
        isStreaming: false,
        error: error.message,
      }));
    },
  };
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  isRequired: null,
  isCheckingStatus: false,
  messages: [],
  isStreaming: false,
  isComplete: false,
  error: null,

  checkStatus: async () => {
    set({ isCheckingStatus: true });
    try {
      const result = await onboardingApi.getStatus();
      set({ isRequired: result.required, isCheckingStatus: false });
    } catch {
      // Fail open - don't block user if status check fails
      set({ isRequired: false, isCheckingStatus: false });
    }
  },

  startConversation: async () => {
    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;

    const streamingId = `streaming-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    set({ messages: [assistantPlaceholder], isStreaming: true, error: null });

    try {
      await streamChat(
        "/onboarding/chat",
        { messages: [] },
        token,
        createStreamingCallbacks(set, streamingId),
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to start conversation";
      set((state) => ({
        messages: state.messages.filter((m) => m.id !== streamingId),
        isStreaming: false,
        error: message,
      }));
    }
  },

  sendMessage: async (content: string) => {
    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;

    // Create optimistic user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date().toISOString(),
    };

    // Create streaming assistant placeholder
    const streamingId = `streaming-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    // Build messages to send to backend (full history + new user message, excluding placeholder)
    const messagesToSend = [...get().messages, userMessage];

    // Add both messages and set streaming
    set((state) => ({
      messages: [...state.messages, userMessage, assistantPlaceholder],
      isStreaming: true,
      error: null,
    }));

    try {
      await streamChat(
        "/onboarding/chat",
        { messages: messagesToSend },
        token,
        createStreamingCallbacks(set, streamingId, userMessage.id),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to send message";
      set((state) => ({
        messages: state.messages.filter(
          (m) => m.id !== streamingId && m.id !== userMessage.id,
        ),
        isStreaming: false,
        error: message,
      }));
    }
  },

  completeOnboarding: async () => {
    try {
      await onboardingApi.complete();
      set({ isRequired: false });
    } catch {
      // Even if the API call fails, let the user proceed
      set({ isRequired: false });
    }
  },

  reset: () => {
    set({
      isRequired: null,
      isCheckingStatus: false,
      messages: [],
      isStreaming: false,
      isComplete: false,
      error: null,
    });
  },
}));
